/**
 * features/hr/LoanActionsModal.tsx
 * ------------------------------------------------------------------
 * The three things that actually happen to a loan after it is set up:
 * the instalment changes, the borrower tops up, or it finishes.
 *
 * None of them were doable from the app. Editing the loan's monthly amount
 * silently rewrote history — the balance engine assumed one amount for the
 * whole life of the loan, so changing it moved every past balance too. There
 * was no way at all to record a top-up except by creating a second loan,
 * which is how one staff member ended up with two and nobody could say what
 * they owed in a single figure.
 *
 * So each action APPENDS to a history rather than overwriting a field:
 *
 *   Change the instalment  -> values.instalmentChanges  [{ fromMonth, amount }]
 *   Top up                 -> values.topUps             [{ month, amount }]
 *
 * The balance for any month is then a pure function of those, which is why a
 * payslip re-rendered months later still computes the same figures.
 *
 * WHAT EACH LOAN TYPE GETS. A bank loan has no balance — the school is never
 * told what was borrowed — so topping up there is nothing more than a new
 * monthly amount from a given month, and "stop" is the only ending it has. A
 * school loan or advance carries a balance, so it gets both, and stopping one
 * early is a decision about money still owed rather than an administrative
 * tidy-up. The wording differs accordingly; a single generic dialog would
 * have made an unpaid balance sound like a completed loan.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { X, TrendingUp, CalendarClock, CircleStop, Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { auditFromContext } from '@/lib/audit';
import { hr } from '@/features/hr/hrWrite';
import {
  DEDUCTION_TYPES, deductionForPeriod, deductionMonthlyAmount, instalmentForPeriod,
  currentYearMonth, fmtPula, round2,
  type HrDeduction, type InstalmentChange, type LoanTopUp,
} from '@/lib/hrPayroll';

type Action = 'instalment' | 'topup' | 'stop';

const ymKey = (p: { year: number; month: number }) => `${p.year}-${String(p.month).padStart(2, '0')}`;

/** The next few months a change can be dated from, starting with this one. */
function monthOptions(from: { year: number; month: number }, count = 6): string[] {
  const out: string[] = [];
  let { year, month } = from;
  for (let i = 0; i < count; i++) {
    out.push(ymKey({ year, month }));
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }
  return out;
}

export default function LoanActionsModal({
  deduction: d,
  staffName,
  onClose,
}: {
  deduction: HrDeduction;
  staffName: string;
  onClose: () => void;
}) {
  const { userData } = useAuth();
  const cfg = DEDUCTION_TYPES[d.type];
  const carriesBalance = !!cfg.balanceKey;

  const period = currentYearMonth();
  const nowKey = ymKey(period);
  const current = deductionForPeriod(d, period);

  const [action, setAction] = useState<Action>(d.type === 'salary_advance' ? 'stop' : 'instalment');
  const [fromMonth, setFromMonth] = useState(nowKey);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /* `values` is a loose bag, so the two histories come back as a union of both
     array shapes. Narrowed once here rather than at every use. */
  const existingChanges = (Array.isArray(d.values.instalmentChanges) ? d.values.instalmentChanges : []) as InstalmentChange[];
  const existingTopUps = (Array.isArray(d.values.topUps) ? d.values.topUps : []) as LoanTopUp[];

  const amountNum = round2(Number(amount) || 0);

  async function submit() {
    setErr(null);
    if (action !== 'stop') {
      if (amount.trim() === '') { setErr('Enter an amount.'); return; }
      if (amountNum < 0) { setErr('An amount cannot be negative.'); return; }
      if (action === 'topup' && amountNum <= 0) { setErr('A top-up must be more than nil.'); return; }
    }
    setBusy(true);
    try {
      if (action === 'instalment') {
        // Appended, never overwritten: the old amount stays true for the
        // months it applied to.
        const next = [
          ...existingChanges.filter((c) => c.fromMonth !== fromMonth),
          { fromMonth, amount: amountNum, note: note.trim() || undefined, by: userData?.displayName, at: new Date().toISOString() },
        ].sort((a, b) => (a.fromMonth < b.fromMonth ? -1 : 1));
        await hr.deductions.update(d.id, { values: { ...d.values, instalmentChanges: next } });
        auditFromContext(userData, 'loan_instalment_changed', 'hr', {
          targetId: d.staffId, targetType: 'user', targetName: staffName,
          details: { deductionId: d.id, type: d.type, fromMonth, amount: amountNum, previous: instalmentForPeriod(d, period), note: note.trim() || null },
        });
      } else if (action === 'topup') {
        const next = [
          ...existingTopUps,
          { month: fromMonth, amount: amountNum, note: note.trim() || undefined, by: userData?.displayName, at: new Date().toISOString() },
        ].sort((a, b) => (a.month < b.month ? -1 : 1));
        await hr.deductions.update(d.id, { values: { ...d.values, topUps: next } });
        auditFromContext(userData, 'loan_topped_up', 'hr', {
          targetId: d.staffId, targetType: 'user', targetName: staffName,
          details: { deductionId: d.id, type: d.type, month: fromMonth, amount: amountNum, note: note.trim() || null },
        });
      } else {
        await hr.deductions.update(d.id, { status: 'paid_off' });
        auditFromContext(userData, 'loan_stopped', 'hr', {
          targetId: d.staffId, targetType: 'user', targetName: staffName,
          details: {
            deductionId: d.id, type: d.type, stoppedFrom: nowKey,
            lastAmount: deductionMonthlyAmount(d),
            balanceAtStop: carriesBalance ? current.remainingAfter : null,
            note: note.trim() || null,
          },
        });
      }
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const tabs: { key: Action; label: string; icon: typeof TrendingUp; show: boolean }[] = [
    /* A salary advance comes off whole in one payroll (26 Oct 2026): no
       changing its instalment, no topping it up — a further advance is a new one. */
    { key: 'instalment', label: 'Change instalment', icon: CalendarClock, show: d.type !== 'salary_advance' },
    { key: 'topup', label: 'Top up', icon: TrendingUp, show: carriesBalance && d.type !== 'salary_advance' },
    { key: 'stop', label: 'Stop', icon: CircleStop, show: true },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 dark:text-white truncate">{d.label}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {staffName} · deducting {fmtPula(current.amount)} in {nowKey}
              {carriesBalance && current.remainingAfter !== null && ` · ${fmtPula(current.remainingAfter)} left after`}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 shrink-0"><X size={18} /></button>
        </div>

        <div className="flex border-b border-gray-100 dark:border-gray-700">
          {tabs.filter((t) => t.show).map((t) => {
            const Icon = t.icon;
            const on = t.key === action;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => { setAction(t.key); setErr(null); }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  on ? 'border-primary text-primary dark:text-primarySoft' : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-white'
                }`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="px-5 py-4 space-y-3">
          {action === 'instalment' && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Set what comes off from a given month. Everything before it stays as it was.
                {carriesBalance && ' Enter 0 to pause without clearing the balance.'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-500 dark:text-gray-400">
                  From
                  <select value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-900 text-sm text-gray-900 dark:text-white">
                    {monthOptions(period).map((m) => <option key={m} value={m}>{m}{m === nowKey ? ' (this month)' : ''}</option>)}
                  </select>
                </label>
                <label className="text-xs text-gray-500 dark:text-gray-400">
                  New monthly amount
                  <input
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                    inputMode="decimal"
                    placeholder={String(deductionMonthlyAmount(d))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-900 text-sm text-right text-gray-900 dark:text-white"
                  />
                </label>
              </div>
            </>
          )}

          {action === 'topup' && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Added to what is already owed, from the start of the month you choose. This does not create a
                second loan — there is one debt and one balance.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-500 dark:text-gray-400">
                  From
                  <select value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-900 text-sm text-gray-900 dark:text-white">
                    {monthOptions(period).map((m) => <option key={m} value={m}>{m}{m === nowKey ? ' (this month)' : ''}</option>)}
                  </select>
                </label>
                <label className="text-xs text-gray-500 dark:text-gray-400">
                  Amount borrowed
                  <input
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                    inputMode="decimal"
                    placeholder="0.00"
                    className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-900 text-sm text-right text-gray-900 dark:text-white"
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                If the instalment is changing too, record that separately under “Change instalment”. Keeping them
                apart means the record says what was borrowed and what was agreed, rather than merging the two.
              </p>
            </>
          )}

          {action === 'stop' && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Stops the deduction from the next pay run. Nothing already deducted is affected.
              </p>
              {carriesBalance && current.remainingAfter !== null && current.remainingAfter > 0 && (
                <p className="text-sm inline-flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-amber-800 dark:text-amber-200">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                  <span>
                    {fmtPula(current.remainingAfter)} is still outstanding. Stopping now leaves that owed and no
                    longer being collected — say why below.
                  </span>
                </p>
              )}
              {!carriesBalance && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  The school is not told a bank loan&rsquo;s balance, so stopping is the only ending it has. Do this
                  when the bank confirms it is settled.
                </p>
              )}
            </>
          )}

          <label className="block text-xs text-gray-500 dark:text-gray-400">
            Note {action === 'stop' ? '(recommended)' : '(optional)'}
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={action === 'stop' ? 'e.g. bank confirmed settled 21 Aug' : 'e.g. agreed with the bursar'}
              className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-900 text-sm text-gray-900 dark:text-white"
            />
          </label>

          {(existingChanges.length > 0 || existingTopUps.length > 0) && (
            <details className="text-xs text-gray-500 dark:text-gray-400">
              <summary className="cursor-pointer">History ({existingChanges.length + existingTopUps.length})</summary>
              <ul className="mt-2 space-y-0.5">
                {existingTopUps.map((t, i) => (
                  <li key={`t${i}`}>{t.month} · topped up {fmtPula(t.amount)}{t.by ? ` · ${t.by}` : ''}{t.note ? ` — ${t.note}` : ''}</li>
                ))}
                {existingChanges.map((c, i) => (
                  <li key={`c${i}`}>{c.fromMonth} · instalment set to {fmtPula(c.amount)}{c.by ? ` · ${c.by}` : ''}{c.note ? ` — ${c.note}` : ''}</li>
                ))}
              </ul>
            </details>
          )}

          {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-600 text-sm font-semibold hover:border-gray-300 disabled:opacity-40">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className={`px-4 py-2 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 ${
              action === 'stop' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-primaryLight'
            }`}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}
            {action === 'instalment' ? 'Save change' : action === 'topup' ? 'Record top-up' : 'Stop deduction'}
          </button>
        </div>
      </div>
    </div>
  );
}
